import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { useApp } from "./store";

/**
 * Loads from the API and refetches whenever a server event arrives.
 *
 * The backend is the source of truth, so views re-read rather than patching local copies from socket
 * payloads; the socket only says "something changed".
 */
export function useApiData<T>(path: string | null): {
  data: T | null;
  error: unknown;
  reload: () => void;
} {
  const { revision } = useApp();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [manualRevision, setManualRevision] = useState(0);

  useEffect(() => {
    if (!path) {
      return;
    }
    let cancelled = false;
    api
      .get<T>(path)
      .then((loaded) => {
        if (!cancelled) {
          setData(loaded);
          setError(null);
        }
      })
      .catch((caught) => {
        if (!cancelled) {
          setError(caught);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [path, revision, manualRevision]);

  const reload = useCallback(() => setManualRevision((current) => current + 1), []);
  return { data, error, reload };
}

/**
 * The number filter shared by all three views. Selecting a number suppresses the number label in rows,
 * which is the design's display rule; the backend applies it, this only chooses the filter.
 */
export function NumberFilter({
  value,
  onChange
}: {
  value: string | null;
  onChange: (numberId: string | null) => void;
}): JSX.Element {
  const { numbers } = useApp();
  return (
    <div className="chips">
      <button
        type="button"
        className={value === null ? "chip active" : "chip"}
        onClick={() => onChange(null)}
      >
        All numbers
      </button>
      {numbers
        .filter((number) => number.state !== "RELEASED")
        .map((number) => (
          <button
            key={number.id}
            type="button"
            className={value === number.id ? "chip active" : "chip"}
            onClick={() => onChange(number.id)}
          >
            {number.e164}
          </button>
        ))}
    </div>
  );
}

/** Query-string helper for the number filter. */
export function withNumberFilter(path: string, numberId: string | null): string {
  return numberId ? `${path}?numberId=${encodeURIComponent(numberId)}` : path;
}
